import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";

import CategoryIcon from "@mui/icons-material/Category";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InventoryIcon from "@mui/icons-material/Inventory";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SettingsIcon from "@mui/icons-material/Settings";

import Logo from "../assets/logo.png";

import "./Sidebar.css";

const navItems = [
  { label: "Dashboard", to: "/admin/dashboard", icon: <DashboardIcon />, match: "dashboard" },
  { label: "Category", to: "/admin/category", icon: <CategoryIcon />, match: "category" },
  { label: "Products", to: "/admin/products", icon: <InventoryIcon />, match: "products" },
];

export default function Sidebar({ isOpen, isMobile }) {
  const location = useLocation();
  const [openSettings, setOpenSettings] = useState(false);

  // Desktop: 248px expanded / 78px collapsed
  // Mobile: 248px slide-over / completely hidden
  const drawerWidth = isMobile ? 248 : isOpen ? 248 : 78;

  const isActive = (match) => location.pathname.includes(match);

  const button = (item) => {
    const active = isActive(item.match);
    return (
      <ListItemButton
        key={item.label}
        component={Link}
        to={item.to}
        className={`admin-sidebar-link${active ? " active" : ""}`}
      >
        <ListItemIcon className="admin-sidebar-icon">{item.icon}</ListItemIcon>
        {isOpen && <ListItemText primary={item.label} />}
      </ListItemButton>
    );
  };

  return (
    <Drawer
      // On mobile: temporary (slide-over, closes on backdrop click)
      // On desktop: permanent (always mounted, width animates)
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? isOpen : true}
      onClose={() => {}} // handled by overlay in AdminLayout
      className={`admin-sidebar${isOpen ? " expanded" : " collapsed"}`}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          background:
            "linear-gradient(180deg, #0b1a4a 0%, #0f2d70 54%, #07143a 100%)",
          color: "#fff",
          borderRight: "1px solid rgba(147, 197, 253, 0.16)",
          boxShadow: "18px 0 44px rgba(15, 23, 42, 0.16)",
          transition: "width 0.28s ease !important",
          overflowX: "hidden",
        },
        // Remove MUI's built-in backdrop — we use our own overlay
        "& .MuiBackdrop-root": { display: "none" },
      }}
      PaperProps={{ className: "admin-sidebar-paper" }}
      ModalProps={{ keepMounted: true }}
    >
      <div className="admin-sidebar-brand">
        <img src={Logo} alt="Tecniqa" className="admin-sidebar-logo" />
      </div>

      {isOpen && (
        <div className="admin-sidebar-intro">
          <p>Admin Panel</p>
          <span>Store operations</span>
        </div>
      )}

      <List className="admin-sidebar-list">
        {navItems.map((item) =>
          isOpen ? (
            button(item)
          ) : (
            <Tooltip title={item.label} placement="right" key={item.label}>
              {button(item)}
            </Tooltip>
          )
        )}

        <ListItemButton
          onClick={() => setOpenSettings((o) => !o)}
          className={`admin-sidebar-link${openSettings ? " active-soft" : ""}`}
        >
          <ListItemIcon className="admin-sidebar-icon">
            <SettingsIcon />
          </ListItemIcon>
          {isOpen && <ListItemText primary="Settings" />}
          {isOpen && (openSettings ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>

        <Collapse in={openSettings && isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding className="admin-sidebar-sublist">
            <ListItemButton
              component={Link}
              to="/admin/user"
              className={`admin-sidebar-sublink${isActive("user") ? " active" : ""}`}
            >
              <ListItemIcon className="admin-sidebar-icon sub">
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText primary="User" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>

      <div className="admin-sidebar-footer">
        {isOpen ? (
          <>
            <strong>Tecniqa</strong>
            <span>E-commerce Control</span>
          </>
        ) : (
          <span className="admin-sidebar-footer-dot" />
        )}
      </div>
    </Drawer>
  );
}